import * as bom from './bom';
import { getGlobalData } from './global';

export enum NetWorkType {
  DEFAULT = -1,
  UNKNOW = 0,
  NONE = 0,
  WIFI = 1,
  EDGE = 2,
  WWAN = 3,
  '2G' = 4,
  '3G' = 5,
  '4G' = 6,
  '5G' = 7
}

/**
 * 获取 UA 信息，会做统一小写化处理
 *
 * @returns
 */
export const getUA = () => {
  let ua: string =
    typeof document !== 'undefined'
      ? window?.navigator?.userAgent
      : getGlobalData().request?.headers['user-agent'] || '';

  ua = ua?.toLowerCase();
  return ua;
};

/**
 * 判断是否不是手机或平板
 *
 * @returns
 * @category 环境判断
 */
export const isPC = () => {
  return !isMobile();
};

/**
 * 判断是否是移动终端（包含手机和平板）
 *
 * @returns
 * @category 环境判断
 */
export const isMobile = () => {
  return (
    /Android|iPhone|iPod|BlackBerry|IEMobile|MiniProgram/i.test(getUA()) ||
    isIPad()
  );
};

/**
 * 判断是否 iOS 系统，包含 iphone/ipod/ipad
 *
 * @category 环境判断
 * @returns
 */
export const isIOS = () => {
  return /iPhone|iPod|iPad/i.test(getUA());
};

/**
 * 判断是否 iPad
 *
 * @category 环境判断
 * @returns
 */
export const isIPad = () => {
  return /iPad/i.test(getUA());
};

/**
 * 判断是否 iPhone X 以及刘海屏系列
 *
 * @category 环境判断
 * @returns
 */
export const isIPhoneX = (): boolean => {
  return (
    isIOS() &&
    ((window.screen.width === 375 && window.screen.height === 812) || // X, XS, 11 Pro
      (window.screen.width === 414 && window.screen.height === 896))
  ); // XS Max, XR, 11, 11 Pro Max
};

/**
 * 判断是否安卓系统
 *
 * @category 环境判断
 * @returns
 */
export const isAndroid = () => {
  return getUA().indexOf('android') !== -1;
};

/**
 * 判断是否微信浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isWeixin = () => {
  return getUA().indexOf('micromessenger') !== -1;
};

/**
 * 判断是否 QQ 浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isQQBrowser = () => {
  return getUA().indexOf('mqqbrowser') !== -1;
};

/**
 * 判断是否 safari 浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isSafariBrowser = () => {
  return /^((?!chrome|android|crios).)*safari/i.test(getUA());
};

/**
 * 判断是否 x5内核浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isX5 = () => {
  return getUA().indexOf('tbs/') !== -1;
};

/**
 * 判断是否 UC 浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isUCBrowser = () => {
  return getUA().indexOf('ucbrowser') !== -1;
};

/**
 * 判断是否 IE 浏览器（注意不包括 Edge ）
 *
 * @category 环境判断
 * @returns
 */
export const isIE = () => {
  const ua = getUA();
  return (
    ua.indexOf('msie') > -1 ||
    (ua.indexOf('trident') > -1 && ua.indexOf('rv:11.0') > -1)
  );
};

/**
 * 判断是否 isEdge 浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isEdge = () => {
  return getUA().indexOf('ucbrowser') !== -1;
};

/**
 *
 * @returns
 */
export const getChromeVersion = () => {
  const matches = getUA().match(/chrome\/([\d.]+)/);
  return matches ? matches[1] : '';
};

/**
 * 获取小米MIUI浏览器版本
 *
 * @category 版本信息
 * @returns
 */
export const getMiuiBrowserVersion = () => {
  const matches = getUA().match(/miuibrowser\/(\d+\.\d+\.\d+)/i);
  return matches ? matches[1] : '';
};

/**
 * 获取x5浏览器版本
 *
 * @category 版本信息
 * @returns
 */
export const getX5Version = () => {
  const matches = getUA().match(/tbs\/(\d\d\d)\d+/i);

  return matches ? parseInt(matches[1], 10) : null;
};

/**
 * 获取 IE 浏览器版本
 *
 * @category 版本信息
 * @returns 正常返回 6/7/8/9/10/11，如果版本低于 6 统一返回 0
 */
export const getIEVersion = () => {
  if (!isIE()) {
    return 0;
  }
  const ua = getUA();
  const matches = ua.match(/msie (\d+)/);
  const version = matches ? parseInt(matches[1], 10) : null;
  if (ua.indexOf('msie 6.') !== -1) {
    return 6;
  }
  if (version === 7 || version === 8 || version === 9 || version === 10) {
    return version;
  }
  if (ua.toLowerCase().match(/rv:([\d.]+)\) like gecko/)) {
    return 11;
  }
  return 0;
};

/**
 * 获取 safari 版本
 *
 * @category 版本信息
 * @returns
 */
export const getSafariVer = () => {
  const matchVer = getUA().match(/version\/([\d.]+).*safari/);
  if (matchVer) {
    return parseInt(matchVer[1], 10);
  }
  return null;
};

/**
 * 获取微信浏览器版本
 *
 * @category 版本信息
 * @returns
 */
export const getWeiXinVersion = () => {
  const matches = getUA().match(/micromessenger\/(\d+\.\d+\.\d+)(\.\d+)?/i);

  return matches ? matches[1] : '';
};

/**
 * 获取QQ内置浏览器版本
 *
 * @category 版本信息
 * @returns
 */
export const getQQVersion = () => {
  const matches = getUA().match(/qq\/(\d+\.\d+(\.\d+)?)/i);

  return matches ? matches[1] : '';
};

/**
 * 判断是否服务器直出页面
 *
 * @category 环境判断
 * @returns
 */
export const isServer = (): boolean => {
  return !(
    typeof window === 'object' &&
    window &&
    typeof document !== 'undefined'
  );
};

/**
 * 判断是否 QQ 内置浏览器
 *
 * @category 环境判断
 * @returns
 */
export const isQQ = () => {
  return !!getQQVersion();
};

/**
 * 判断是否百度 APP
 *
 * @category 环境判断
 * @returns
 */
export const isBaiduApp = () => {
  return getUA().indexOf('baiduboxapp') !== -1;
};

/**
 * 判断是否微博APP
 *
 * @category 环境判断
 * @returns
 */
export const isWeiboApp = () => {
  return getUA().indexOf(' weibo ') !== -1;
};

/**
 * 判断是否 移动端 Chrome 浏览器
 * @see https://github.com/hgoebl/mobile-detect.js/blob/bdf68bf2eb9408d6f5b48253b308c2df90e5eec5/mobile-detect.js#L178
 *
 * @category 环境判断
 * @returns
 */
export const isChromeBrowser = () => {
  // 排除 Android 微信干扰
  return (
    !isWeixin() &&
    /\bCrMo\b|CriOS|Android.*Chrome\/[.0-9]* (Mobile)?/i.test(getUA())
  );
};

/**
 * 获取 iOS 或 安卓的版本
 *
 * @category 版本信息
 * @returns
 */
export const getOSVersion = () => {
  let matches: RegExpMatchArray | null;
  let version = '';
  if (isIOS()) {
    matches = getUA().match(/ os ([\d_]+) /i);
    version = matches ? matches[1] : '';
  } else if (isAndroid()) {
    matches = getUA().match(/android( |\/)([\d.]+)/i);
    version = matches ? matches[2] : '';
  }

  return version.replace(/_/gi, '.');
};

/**
 * 判断是否小程序（包含微信/QQ）
 *
 * @category 小程序
 * @param {string} [appkey] 小程序名
 * @returns
 */
export const isMiniProgram = (appkey?: string): boolean => {
  // 由于安卓不准，所以建议使用url的参数来确定是不是小程序
  const isMp = !!bom.getQuery('mp');

  // 小程序web-view环境相关接口说明
  // 微信：https://developers.weixin.qq.com/miniprogram/dev/component/web-view.html
  // QQ：https://q.qq.com/wiki/develop/miniprogram/component/open-ability/web-view.html

  // 微信环境相关接口4 || QQ环境相关接口4
  const mpEnv4 =
    getGlobalData().__wxjs_environment === 'miniprogram' ||
    getGlobalData().__qqjs_environment === 'miniprogram';
  // 微信或QQ环境相关接口5
  const mpEnv5 = getUA().indexOf('miniprogram') !== -1;

  const isInMp = mpEnv5 || mpEnv4 || isMp;

  if (appkey) {
    return isInMp && bom.getUrlParameter('sfrom') === appkey;
  }

  return isInMp;
};

/**
 * 判断是否微信小程序（不特指课堂小程序）
 *
 * @category 小程序
 * @param {string} [appkey] 小程序名
 * @returns
 */
export const isWXMiniProgram = (appkey?: string) => {
  return isWeixin() && isMiniProgram(appkey);
};

/**
 * 判断是否QQ小程序（不特指课堂小程序）
 *
 * @category 小程序
 * @param {string} [appkey] 小程序名
 * @returns
 */
export const isQQMiniProgram = (appkey?: string) => {
  return isQQ() && isMiniProgram(appkey);
};

/**
 * 判断是否为 iOS 小程序
 *
 * @category 小程序
 * @returns
 */
export const isIOSMiniProgram = () => {
  return (isIOS() || isIPad()) && isMiniProgram();
};

/**
 * 获取当前系统环境，返回 ios/android/h5
 *
 * @export
 * @returns
 */
export const getEnv = () => {
  if (isIOS()) {
    return 'ios';
  }
  if (isAndroid()) {
    return 'android';
  }
  // @TODO 这里应该返回web？
  return 'h5';
};

/**
 * 获取当前的Web运行环境
 *
 * @export
 * @returns
 */
export const getRuntimeEnv = () => {
  const origin = window.location.origin;
  const env = {
    isTest: /.qa|(\.qa)/.test(origin),
    isDev: /.dev|(\.dev)/.test(origin),
    isRelease: /.release|(\.release)/.test(origin)
  };
  if (env.isTest) {
    return 'test';
  }
  if (env.isDev) {
    return 'dev';
  }
  if (env.isRelease) {
    return 'release';
  }
  return 'pro';
};

/**
 * 是否在企业微信内
 *
 * @export
 * @returns
 */
export const isWxWork = () => {
  const ua = getUA();
  return ua.indexOf('wxwork') !== -1 && ua.indexOf('micromessenger') !== -1;
};

/**
 * 判断系统偏好是否为 dark mode
 *
 * @return {*}
 * @memberof Env
 */
export const isDarkModeInSystem = () => {
  if (
    window?.matchMedia &&
    window?.matchMedia('(prefers-color-scheme: dark)')?.matches
  ) {
    return true;
  }
  return false;
};

/**
 * 判断是否为手 Q 中的夜间模式
 * 文档：https://doc.weixin.qq.com/doc/w3_m_HKyFSZFKYiuV?scode=AJEAIQdfAAo1TTJ1UqAK8ABwaLACc
 *
 * @return {*}
 * @memberof Env
 */
export const isDarkModeInQQ = () => {
  if (isQQ()) {
    const userAgent = getUA();
    if (userAgent.indexOf('qqtheme') >= 0) {
      const [, matchResult] = userAgent.match(/qqtheme\/([^\s]+)/) || [];
      if (
        (isIOS() && (matchResult === '1102' || matchResult === '2920')) ||
        (isAndroid() && (matchResult === '1103' || matchResult === '2920'))
      ) {
        return true;
      }
    }
  }
  return false;
};

/**
 * 判断是否为系统级/手 Q 内的深色模式
 *
 * @return {*}  {boolean}
 * @memberof Env
 */
export const isDarkMode = (): boolean => {
  if (isQQ()) {
    return isDarkModeInQQ();
  }

  if (isMiniProgram()) {
    return false;
  }

  return isDarkModeInSystem();
};
